import { useState } from 'react';
import { Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { RecentOpensFeed } from '@/components/RecentOpensFeed';
import { useIsMobile } from '@/hooks/useIsMobile';

export interface RecentOpensPopoverProps {
  accountId: string;
  limit?: number;
  trigger?: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  onMobileOpen?: () => void;
}

export function RecentOpensPopover({
  accountId,
  limit = 20,
  trigger,
  side = 'right',
  align = 'end',
  sideOffset = 16,
  onMobileOpen,
}: RecentOpensPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && onMobileOpen && isMobile) {
      onMobileOpen();
    }
  };

  const defaultTrigger = (
    <Button variant="ghost" className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground">
      <Activity className="h-4 w-4" />
      Recent Opens
    </Button>
  );

  const content = <RecentOpensFeed accountId={accountId} limit={limit} />;
  const dialogContent = <RecentOpensFeed accountId={accountId} limit={limit} inDialog />;

  return (
    <>
      {/* Desktop: Popover */}
      <div className="hidden md:block">
        <Popover>
          <PopoverTrigger asChild>
            {trigger || defaultTrigger}
          </PopoverTrigger>
          <PopoverContent
            side={side}
            align={align}
            sideOffset={sideOffset}
            className="w-80 p-0 ml-2 !border-primary/50 !bg-card shadow-xl"
          >
            {content}
          </PopoverContent>
        </Popover>
      </div>

      {/* Mobile: Dialog */}
      <div className="md:hidden">
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            {trigger || defaultTrigger}
          </DialogTrigger>
          <DialogContent
            className="w-[calc(100vw-2rem)] max-w-md p-0 shadow-xl"
          >
            {dialogContent}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
